/**
 * ConfigPage — Typed Config Schema UI
 * Auto-renders 4 patterns: dropdown (tag chips), key_value (settings form),
 * structured (table), tags (multi-tag input)
 *
 * BUG FIXES:
 * - Auto-select first category on load
 * - Invalidate both config-categories + config-category on mutations
 * - Delete confirmation dialog
 * - Duplicate name validation
 * - Number field NaN fix
 * - Loading skeleton
 * - Add new key-value capability
 */
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { cn, viIncludes } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Settings2,
  Package,
  ClipboardCheck,
  CalendarRange,
  Warehouse,
  Layers,
  MoreHorizontal,
  ShoppingCart,
  Factory,
  Wrench,
  Truck,
  FlaskConical,
  DollarSign,
  Plus,
  X,
  Pencil,
  Trash2,
  Check,
  Save,
  FolderOpen,
  Search,
} from "lucide-react";
import { toast } from "sonner";

// ── Section definitions ──
const SECTIONS = [
  { key: "system", label: "Hệ thống", icon: Settings2 },
  { key: "master", label: "Danh mục chung", icon: Layers },
  { key: "finance", label: "Tài chính", icon: DollarSign },
  { key: "sales", label: "Kinh doanh", icon: ShoppingCart },
  { key: "product", label: "Sản phẩm", icon: Package },
  { key: "material", label: "Vật tư", icon: FlaskConical },
  { key: "supply", label: "Nhà cung cấp", icon: Truck },
  { key: "planning", label: "Kế hoạch", icon: CalendarRange },
  { key: "technical", label: "Kỹ thuật", icon: Wrench },
  { key: "production", label: "Sản xuất", icon: Factory },
  { key: "quality", label: "Chất lượng", icon: ClipboardCheck },
  { key: "logistics", label: "Kho vận", icon: Warehouse },
  { key: "other", label: "Khác", icon: MoreHorizontal },
];

/**
 * Section trong DB ghi không thống nhất (System / system, Technical, Master, costing...).
 * So khớp không phân biệt hoa thường + quy đổi; section lạ vào "Khác" — không danh mục nào bị ẩn
 * (trước đây chỉ hiện 4/50 danh mục vì so khớp đúng chữ thường).
 */
const SECTION_ALIAS: Record<string, string> = {
  system: "system", master: "master", finance: "finance", costing: "finance", estimating: "finance",
  sales: "sales", product: "product", material: "material", supply: "supply", purchasing: "supply",
  planning: "planning", technical: "technical", production: "production", quality: "quality",
  logistics: "logistics", warehouse: "logistics",
};
const sectionKeyOf = (c: { section?: string | null }) => SECTION_ALIAS[String(c?.section || "").trim().toLowerCase()] || "other";

/** Helper: invalidate both sidebar counts + category items */
async function invalidateConfig(queryClient: any, categoryCode: string) {
  await queryClient.invalidateQueries({ queryKey: ["config-categories"] });
  await queryClient.invalidateQueries({
    queryKey: ["config-category", categoryCode],
  });
}

// Sections expanded by default (all except system)
const DEFAULT_EXPANDED = new Set([
  "finance",
  "sales",
  "product",
  "material",
  "supply",
  "planning",
  "technical",
  "production",
  "quality",
  "logistics",
]);

export default function ConfigPage() {
  const queryClient = useQueryClient();
  const [expandedSections, setExpandedSections] =
    useState<Set<string>>(DEFAULT_EXPANDED);
  const [selectedCategoryCode, setSelectedCategoryCode] = useState<
    string | null
  >(null);
  const [sidebarSearch, setSidebarSearch] = useState("");

  // ── Fetch all categories ──
  const {
    data: categories = [],
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useQuery({
    queryKey: ["config-categories"],
    queryFn: () => trpcClient.config.getCategories.query(),
  });

  // ── Fetch selected category with items ──
  const {
    data: categoryData,
    isLoading: itemsLoading,
    error: itemsError,
  } = useQuery({
    queryKey: ["config-category", selectedCategoryCode],
    queryFn: () =>
      selectedCategoryCode
        ? trpcClient.config.getCategoryWithItems.query({
            code: selectedCategoryCode,
          })
        : null,
    enabled: !!selectedCategoryCode,
  });

  // Auto-select first category on initial load
  useEffect(() => {
    if (!selectedCategoryCode && (categories as any[]).length > 0) {
      // Pick first from any expanded section
      for (const section of SECTIONS) {
        if (expandedSections.has(section.key)) {
          const first = (categories as any[]).find(
            (c) => sectionKeyOf(c) === section.key,
          );
          if (first) {
            setSelectedCategoryCode(first.code);
            break;
          }
        }
      }
    }
  }, [categories, expandedSections, selectedCategoryCode]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col md:flex-row">
      {/* ═══ LEFT SIDEBAR — Sections + Categories ═══ (điện thoại: xếp trên, cao tối đa 38vh, cuộn được) */}
      <div className="w-full max-h-[38vh] border-b md:w-[240px] md:max-h-none md:border-b-0 md:border-r border-slate-200 bg-white flex flex-col shrink-0">
        {/* Search */}
        <div className="px-3 py-2 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              placeholder="Tìm danh mục..."
              value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.target.value)}
              className="h-7 text-xs pl-7"
            />
          </div>
        </div>

        <ScrollArea className="flex-1">
          {categoriesLoading ? (
            <div className="p-3 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full" />
              ))}
            </div>
          ) : (
            SECTIONS.map((section) => {
              const Icon = section.icon;
              const isExpanded = expandedSections.has(section.key);
              const allCats = (categories as any[]).filter(
                (c) => sectionKeyOf(c) === section.key,
              );
              const cats = sidebarSearch
                ? allCats.filter((c) => viIncludes(c.name, sidebarSearch))
                : allCats;

              // Hide section if search has no matches
              if (sidebarSearch && cats.length === 0) return null;
              // Hide section if it has no categories
              if (allCats.length === 0) return null;

              return (
                <div key={section.key}>
                  {/* Section Header — click to toggle expand/collapse */}
                  <button
                    className={cn(
                      "w-full px-3 py-2 text-left flex items-center gap-2 text-xs font-bold uppercase tracking-wider transition-colors",
                      isExpanded
                        ? "bg-slate-50 text-slate-700"
                        : "text-slate-400 hover:bg-slate-50",
                    )}
                    onClick={() => toggleSection(section.key)}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {section.label}
                    <Badge
                      variant="secondary"
                      className="ml-auto text-[9px] px-1 py-0 bg-slate-100 font-bold"
                    >
                      {allCats.length}
                    </Badge>
                  </button>

                  {/* Category Items (expanded when section is toggled or searching) */}
                  {(isExpanded || sidebarSearch) &&
                    cats.map((cat) => (
                      <button
                        key={cat.code}
                        className={cn(
                          "w-full pl-8 pr-3 py-1.5 text-left text-xs flex items-center justify-between transition-colors",
                          selectedCategoryCode === cat.code
                            ? "bg-indigo-50/50 text-indigo-700 font-semibold"
                            : "text-slate-600 hover:bg-slate-50",
                        )}
                        onClick={() => {
                          setSelectedCategoryCode(cat.code);
                          if (!expandedSections.has(cat.section))
                            toggleSection(cat.section);
                        }}
                      >
                        <span className="truncate">{cat.name}</span>
                        <Badge
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0 bg-slate-100 shrink-0 ml-1"
                        >
                          {cat._count?.items || 0}
                        </Badge>
                      </button>
                    ))}
                </div>
              );
            })
          )}
        </ScrollArea>
      </div>

      {/* ═══ MAIN CONTENT ═══ */}
      <div className="flex-1 flex flex-col min-w-0 bg-slate-50/50">
        {itemsLoading ? (
          <div className="p-6 space-y-3">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : categoryData ? (
          <PatternRenderer
            category={categoryData as any}
            queryClient={queryClient}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center">
              <FolderOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">Chọn một danh mục cấu hình</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// PATTERN RENDERER — Auto-render by category.pattern
// ═══════════════════════════════════════════════════

function PatternRenderer({
  category,
  queryClient,
}: {
  category: any;
  queryClient: any;
}) {
  const { pattern, name, items } = category;
  // Tìm trong mục của danh mục đang mở (tên, mã, giá trị) — FB-EPY2F6; đổi danh mục thì xoá ô tìm
  const [itemSearch, setItemSearch] = useState("");
  useEffect(() => setItemSearch(""), [category?.id]);
  const visibleItems = itemSearch
    ? (items || []).filter((i: any) =>
        viIncludes([i.name, i.code, i.value, i.key, JSON.stringify(i.data ?? "")].filter(Boolean).join(" "), itemSearch),
      )
    : items || [];
  // allItems: danh sách đầy đủ cho kiểm trùng khi thêm mục (ô tìm chỉ lọc phần hiển thị)
  const shownCategory = itemSearch ? { ...category, items: visibleItems, allItems: items } : category;

  // Map pattern to Vietnamese label
  const patternLabels: Record<string, string> = {
    dropdown: "Danh sách chọn",
    key_value: "Cài đặt",
    structured: "Bảng dữ liệu",
    tags: "Nhóm tag",
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="px-3 md:px-5 py-3 border-b border-slate-200 bg-white flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-sm font-bold text-slate-800 whitespace-nowrap">{name}</h2>
          <Badge variant="outline" className="text-[10px]">
            {patternLabels[pattern] || pattern}
          </Badge>
          <Badge variant="secondary" className="text-[10px]">
            {itemSearch ? `${visibleItems.length}/${items?.length || 0}` : items?.length || 0} mục
          </Badge>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input
            data-testid="config-item-search"
            placeholder={`Tìm trong ${String(name || "").toLowerCase()}...`}
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            className="h-8 pl-7 text-xs"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {itemSearch && visibleItems.length === 0 && (
          <p className="text-sm text-slate-500 py-6 text-center">Không có mục nào khớp "{itemSearch}"</p>
        )}
        {pattern === "dropdown" && (
          <DropdownPattern category={shownCategory} queryClient={queryClient} />
        )}
        {pattern === "key_value" && (
          <KeyValuePattern category={shownCategory} queryClient={queryClient} />
        )}
        {pattern === "structured" && (
          <StructuredPattern category={shownCategory} queryClient={queryClient} />
        )}
        {pattern === "tags" && (
          <TagsPattern category={shownCategory} queryClient={queryClient} />
        )}
      </div>
    </div>
  );
}

// ─── Pattern A: DROPDOWN — Tag chips ───
function DropdownPattern({
  category,
  queryClient,
}: {
  category: any;
  queryClient: any;
}) {
  const [newItem, setNewItem] = useState("");

  const addMutation = useMutation({
    mutationFn: (name: string) =>
      trpcClient.config.addConfigItem.mutate({ categoryId: category.id, name }),
    onSuccess: async () => {
      invalidateConfig(queryClient, category.code);
      setNewItem("");
    },
    onError: (err: any) => toast.error(err?.message || "Lỗi thêm item"),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      trpcClient.config.deleteConfigItem.mutate({ id }),
    onSuccess: async () => {
      invalidateConfig(queryClient, category.code);
    },
    onError: () => toast.error("Lỗi xóa item"),
  });
  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      trpcClient.config.toggleConfigItem.mutate({ id, active }),
    onSuccess: () => invalidateConfig(queryClient, category.code),
  });

  const handleAdd = () => {
    const trimmed = newItem.trim();
    if (!trimmed) return;
    // FIX BUG-2: Check duplicate
    if (
      (category.allItems || category.items || []).some(
        (i: any) => i.name.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      toast.error(`"${trimmed}" đã tồn tại`);
      return;
    }
    addMutation.mutate(trimmed);
  };

  return (
    <Card className="p-5">
      {/* Add input */}
      <div className="flex gap-2 mb-4">
        <Input
          placeholder={`Thêm ${category.name.toLowerCase()}...`}
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          className="h-8 text-sm"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAdd();
          }}
        />
        <Button
          size="sm"
          className="h-8"
          disabled={!newItem.trim() || addMutation.isPending}
          onClick={handleAdd}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Thêm
        </Button>
      </div>

      {/* Chip list */}
      <div className="flex flex-wrap gap-2">
        {[...(category.items || [])]
          .sort((a: any, b: any) =>
            (a?.name || "").localeCompare(b?.name || "", "vi", {
              sensitivity: "base",
            }),
          )
          .map((item: any) => (
            <div
              key={item.id}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                item.active
                  ? "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                  : "bg-slate-100 border-slate-200 text-slate-400 line-through",
              )}
            >
              <span
                className="cursor-pointer"
                onClick={() =>
                  toggleMutation.mutate({ id: item.id, active: !item.active })
                }
                title={item.active ? "Click để tắt" : "Click để bật"}
              >
                {item.name}
              </span>
              {/* FIX BUG-3: Delete confirmation */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="text-slate-400 hover:text-red-500 transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Xác nhận xóa</AlertDialogTitle>
                    <AlertDialogDescription>
                      Bạn có chắc muốn xóa "{item.name}" khỏi {category.name}?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Hủy</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteMutation.mutate(item.id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Xóa
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
      </div>

      {category.items?.length === 0 && (
        <p className="text-slate-400 text-xs mt-2">
          Chưa có mục nào. Nhập tên và nhấn Enter hoặc Thêm.
        </p>
      )}
    </Card>
  );
}

// ─── Pattern B: KEY_VALUE — Settings form ───
function KeyValuePattern({
  category,
  queryClient,
}: {
  category: any;
  queryClient: any;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      trpcClient.config.updateConfigItem.mutate({ id, data }),
    onSuccess: async () => {
      invalidateConfig(queryClient, category.code);
      setEditingId(null);
    },
    onError: () => toast.error("Lỗi cập nhật"),
  });

  // FIX BUG-4: Add new key-value
  const addMutation = useMutation({
    mutationFn: () =>
      trpcClient.config.addConfigItem.mutate({
        categoryId: category.id,
        name: newKey,
        data: { value: newValue },
      }),
    onSuccess: async () => {
      invalidateConfig(queryClient, category.code);
      setNewKey("");
      setNewValue("");
    },
    onError: (err: any) => toast.error(err?.message || "Lỗi thêm"),
  });

  return (
    <Card className="p-0 overflow-hidden">
      {[...(category.items || [])]
        .sort((a: any, b: any) =>
          (a?.name || "").localeCompare(b?.name || "", "vi", {
            sensitivity: "base",
          }),
        )
        .map((item: any) => (
          <div
            key={item.id}
            className="flex items-center gap-4 px-5 py-3 border-b border-slate-100 last:border-0"
          >
            <span className="text-xs font-semibold text-slate-700 w-[160px] shrink-0">
              {item.name}
            </span>
            {editingId === item.id ? (
              <div className="flex-1 flex gap-2">
                <Input
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="h-8 text-sm flex-1"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter")
                      updateMutation.mutate({
                        id: item.id,
                        data: { value: editValue },
                      });
                    if (e.key === "Escape") setEditingId(null);
                  }}
                />
                <Button
                  size="sm"
                  className="h-8"
                  disabled={updateMutation.isPending}
                  onClick={() =>
                    updateMutation.mutate({
                      id: item.id,
                      data: { value: editValue },
                    })
                  }
                >
                  <Save className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8"
                  onClick={() => setEditingId(null)}
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <div className="flex-1 flex items-center gap-2">
                <span className="text-sm text-slate-600 font-mono truncate">
                  {item.data?.value || "—"}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 ml-auto shrink-0"
                  onClick={() => {
                    setEditingId(item.id);
                    setEditValue(item.data?.value || "");
                  }}
                >
                  <Pencil className="w-3 h-3 text-slate-400" />
                </Button>
              </div>
            )}
          </div>
        ))}

      {/* Add new key-value pair */}
      <div className="flex items-center gap-4 px-5 py-3 bg-slate-50/50 border-t border-slate-100">
        <Input
          placeholder="Tên mới..."
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          className="h-7 text-xs w-[160px] shrink-0"
        />
        <div className="flex-1 flex gap-2">
          <Input
            placeholder="Giá trị..."
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className="h-7 text-xs flex-1"
            onKeyDown={(e) => {
              if (e.key === "Enter" && newKey.trim()) addMutation.mutate();
            }}
          />
          <Button
            size="sm"
            className="h-7 text-xs"
            disabled={!newKey.trim() || addMutation.isPending}
            onClick={() => addMutation.mutate()}
          >
            <Plus className="w-3 h-3 mr-1" /> Thêm
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ─── Pattern C: STRUCTURED — Table with labeled columns ───
function StructuredPattern({
  category,
  queryClient,
}: {
  category: any;
  queryClient: any;
}) {
  const fields: any[] = category.fields || [];
  const [newName, setNewName] = useState("");
  const [newData, setNewData] = useState<Record<string, any>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editData, setEditData] = useState<Record<string, any>>({});

  const addMutation = useMutation({
    mutationFn: () =>
      trpcClient.config.addConfigItem.mutate({
        categoryId: category.id,
        name: newName,
        data: newData,
      }),
    onSuccess: async () => {
      invalidateConfig(queryClient, category.code);
      setNewName("");
      setNewData({});
    },
    onError: (err: any) => toast.error(err?.message || "Lỗi thêm item"),
  });
  const updateMutation = useMutation({
    mutationFn: () =>
      trpcClient.config.updateConfigItem.mutate({
        id: editingId!,
        name: editName,
        data: editData,
      }),
    onSuccess: async () => {
      invalidateConfig(queryClient, category.code);
      setEditingId(null);
    },
    onError: () => toast.error("Lỗi cập nhật"),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      trpcClient.config.deleteConfigItem.mutate({ id }),
    onSuccess: async () => {
      invalidateConfig(queryClient, category.code);
    },
    onError: () => toast.error("Lỗi xóa"),
  });

  const startEdit = (item: any) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditData(item.data || {});
  };

  // FIX BUG-5: Handle number field properly
  const handleFieldChange = (
    setter: (fn: (prev: Record<string, any>) => Record<string, any>) => void,
    field: any,
    value: string,
  ) => {
    setter((prev) => ({
      ...prev,
      [field.key]:
        field.type === "number" ? (value === "" ? "" : Number(value)) : value,
    }));
  };

  // Extract unique existing values for suggestions
  const fieldSuggestions = fields.reduce(
    (acc, f) => {
      const values = new Set<string>();
      (category.items || []).forEach((item: any) => {
        if (
          item.data?.[f.key] != null &&
          String(item.data[f.key]).trim() !== ""
        ) {
          values.add(String(item.data[f.key]).trim());
        }
      });
      acc[f.key] = Array.from(values);
      return acc;
    },
    {} as Record<string, string[]>,
  );

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto apv-compact-table">
        <Table>
          <TableHeader className="apv-table-header">
            <TableRow>
              <TableHead className="min-w-[150px]">Tên</TableHead>
              {fields.map((f: any) => (
                <TableHead key={f.key} className="min-w-[120px]">
                  {f.label}
                </TableHead>
              ))}
              <TableHead className="w-[80px] text-right">TT</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Existing items */}
            {[...(category.items || [])]
              .sort((a: any, b: any) =>
                (a?.name || "").localeCompare(b?.name || "", "vi", {
                  sensitivity: "base",
                }),
              )
              .map((item: any) => (
                <TableRow key={item.id}>
                  {editingId === item.id ? (
                    <>
                      <TableCell>
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="h-7 text-xs"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") updateMutation.mutate();
                            if (e.key === "Escape") setEditingId(null);
                          }}
                        />
                      </TableCell>
                      {fields.map((f: any) => (
                        <TableCell key={f.key}>
                          <Input
                            type={
                              f.type === "number"
                                ? "number"
                                : f.type === "time"
                                  ? "time"
                                  : "text"
                            }
                            value={editData[f.key] ?? ""}
                            onChange={(e) =>
                              handleFieldChange(setEditData, f, e.target.value)
                            }
                            className="h-7 text-xs"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") updateMutation.mutate();
                            }}
                            list={`datalist-edit-${category.id}-${f.key}`}
                            autoComplete="off"
                          />
                          <datalist
                            id={`datalist-edit-${category.id}-${f.key}`}
                          >
                            {fieldSuggestions[f.key]?.map((val: string) => (
                              <option key={val} value={val} />
                            ))}
                          </datalist>
                        </TableCell>
                      ))}
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            disabled={updateMutation.isPending}
                            onClick={() => updateMutation.mutate()}
                          >
                            <Check className="w-3 h-3 text-green-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => setEditingId(null)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell className="font-medium text-slate-800 text-xs">
                        {item.name}
                      </TableCell>
                      {fields.map((f: any) => (
                        <TableCell
                          key={f.key}
                          className="text-xs text-slate-600 font-mono"
                        >
                          {item.data?.[f.key] != null
                            ? String(item.data[f.key])
                            : "—"}
                        </TableCell>
                      ))}
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-0 apv-row-actions">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => startEdit(item)}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                              >
                                <Trash2 className="w-3 h-3 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Xác nhận xóa
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Bạn có chắc muốn xóa "{item.name}"?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Hủy</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteMutation.mutate(item.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Xóa
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}

            {/* Add new row */}
            <TableRow className="bg-slate-50/50">
              <TableCell>
                <Input
                  placeholder="Tên mới..."
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="h-7 text-xs"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newName.trim())
                      addMutation.mutate();
                  }}
                />
              </TableCell>
              {fields.map((f: any) => (
                <TableCell key={f.key}>
                  <Input
                    type={
                      f.type === "number"
                        ? "number"
                        : f.type === "time"
                          ? "time"
                          : "text"
                    }
                    placeholder={f.label}
                    value={newData[f.key] ?? ""}
                    onChange={(e) =>
                      handleFieldChange(setNewData, f, e.target.value)
                    }
                    className="h-7 text-xs"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newName.trim())
                        addMutation.mutate();
                    }}
                    list={`datalist-add-${category.id}-${f.key}`}
                    autoComplete="off"
                  />
                  <datalist id={`datalist-add-${category.id}-${f.key}`}>
                    {fieldSuggestions[f.key]?.map((val: string) => (
                      <option key={val} value={val} />
                    ))}
                  </datalist>
                </TableCell>
              ))}
              <TableCell className="text-right">
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  disabled={!newName.trim() || addMutation.isPending}
                  onClick={() => addMutation.mutate()}
                >
                  <Plus className="w-3 h-3 mr-1" /> Thêm
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

// ─── Pattern D: TAGS — Multi-tag with list ───
function TagsPattern({
  category,
  queryClient,
}: {
  category: any;
  queryClient: any;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState("");
  const [editTags, setEditTags] = useState<string[]>([]);

  const updateMutation = useMutation({
    mutationFn: ({ id, tags }: { id: string; tags: string[] }) =>
      trpcClient.config.updateConfigItem.mutate({ id, data: { tags } }),
    onSuccess: async () => {
      invalidateConfig(queryClient, category.code);
      setEditingId(null);
    },
    onError: () => toast.error("Lỗi cập nhật"),
  });

  const startEdit = (item: any) => {
    setEditingId(item.id);
    setEditTags(item.data?.tags || []);
    setTagInput("");
  };

  const addTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !editTags.includes(trimmed)) {
      setEditTags((prev) => [...prev, trimmed]);
      setTagInput("");
    } else if (editTags.includes(trimmed)) {
      toast.error(`Tag "${trimmed}" đã tồn tại`);
    }
  };

  const removeTag = (tag: string) => {
    setEditTags((prev) => prev.filter((t) => t !== tag));
  };

  return (
    <div className="space-y-4">
      {[...(category.items || [])]
        .sort((a: any, b: any) =>
          (a?.name || "").localeCompare(b?.name || "", "vi", {
            sensitivity: "base",
          }),
        )
        .map((item: any) => (
          <Card key={item.id} className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-slate-700 uppercase">
                {item.name}
              </span>
              {editingId === item.id ? (
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    className="h-6 text-[10px] px-2"
                    disabled={updateMutation.isPending}
                    onClick={() =>
                      updateMutation.mutate({ id: item.id, tags: editTags })
                    }
                  >
                    <Save className="w-3 h-3 mr-1" /> Lưu
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-[10px] px-2"
                    onClick={() => setEditingId(null)}
                  >
                    Hủy
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => startEdit(item)}
                >
                  <Pencil className="w-3 h-3 text-slate-400" />
                </Button>
              )}
            </div>

            {editingId === item.id ? (
              <>
                <div className="flex gap-2 mb-2">
                  <Input
                    placeholder="Thêm tag..."
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    className="h-7 text-xs"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    disabled={!tagInput.trim()}
                    onClick={addTag}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {editTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[11px] font-medium"
                    >
                      {tag}
                      <X
                        className="w-2.5 h-2.5 cursor-pointer hover:text-red-500"
                        onClick={() => removeTag(tag)}
                      />
                    </span>
                  ))}
                  {editTags.length === 0 && (
                    <span className="text-xs text-slate-400">
                      Nhập tag và nhấn Enter
                    </span>
                  )}
                </div>
              </>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(item.data?.tags || []).map((tag: string) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[11px] font-medium"
                  >
                    {tag}
                  </span>
                ))}
                {(!item.data?.tags || item.data.tags.length === 0) && (
                  <span className="text-xs text-slate-400">Chưa có tag</span>
                )}
              </div>
            )}
          </Card>
        ))}
    </div>
  );
}
